import { CloseCode } from '@novachat/shared';
const MAX_BACKOFF_MS = 30_000;
/**
 * Keeps one authenticated socket open (design §9): reconnects with exponential backoff and
 * jitter, detects dead connections with its own ping, and refreshes the token once when the
 * server refuses it. Every reconnect receives a fresh `ready` snapshot.
 */
export function createRealtime(options) {
    const { url, getAccessToken, refreshAccessToken, onEvent, onStatus, onSessionEnded, WebSocket: Socket = globalThis.WebSocket, pingIntervalMs = 25_000, pongTimeoutMs = 10_000, random = Math.random, } = options;
    let socket = null;
    let stopped = true;
    let attempt = 0;
    let retriedAuth = false;
    let reconnectTimer;
    let pingTimer;
    let pongTimer;
    const setStatus = (status) => onStatus?.(status);
    function clearTimers() {
        clearTimeout(reconnectTimer);
        clearInterval(pingTimer);
        clearTimeout(pongTimer);
    }
    function scheduleReconnect() {
        if (stopped)
            return;
        setStatus('offline');
        const base = Math.min(MAX_BACKOFF_MS, 1_000 * 2 ** attempt);
        attempt += 1;
        reconnectTimer = setTimeout(connect, base * (0.5 + random()));
    }
    function send(message) {
        if (socket?.readyState === Socket.OPEN)
            socket.send(JSON.stringify(message));
    }
    async function connect() {
        if (stopped)
            return;
        setStatus('connecting');
        const token = retriedAuth ? await refreshAccessToken() : await getAccessToken();
        if (stopped)
            return;
        if (!token)
            return onSessionEnded?.();
        const ws = new Socket(url);
        socket = ws;
        ws.onopen = () => send({ type: 'auth', token });
        ws.onmessage = (message) => {
            let event;
            try {
                event = JSON.parse(String(message.data));
            }
            catch {
                return;
            }
            if (event.type === 'ready') {
                attempt = 0;
                retriedAuth = false;
                setStatus('open');
                pingTimer = setInterval(() => {
                    send({ type: 'ping' });
                    pongTimer ??= setTimeout(() => ws.close(), pongTimeoutMs);
                }, pingIntervalMs);
            }
            if (event.type === 'pong') {
                clearTimeout(pongTimer);
                pongTimer = undefined;
                return;
            }
            onEvent(event);
        };
        ws.onclose = (close) => {
            if (socket !== ws)
                return;
            socket = null;
            clearTimers();
            pongTimer = undefined;
            if (stopped)
                return;
            if (close.code === CloseCode.SessionEnded) {
                stopped = true;
                setStatus('offline');
                return onSessionEnded?.();
            }
            // The token may simply have expired: refresh once before treating it as an outage.
            if (close.code === CloseCode.Unauthorized && !retriedAuth) {
                retriedAuth = true;
                return void connect();
            }
            scheduleReconnect();
        };
    }
    return {
        start() {
            if (!stopped)
                return;
            stopped = false;
            attempt = 0;
            void connect();
        },
        stop() {
            stopped = true;
            clearTimers();
            const ws = socket;
            socket = null;
            ws?.close(1000, 'Client stopped');
            setStatus('offline');
        },
        /** Reconnect right away, e.g. when the browser comes back online. */
        reconnectNow() {
            if (stopped || socket)
                return;
            clearTimeout(reconnectTimer);
            attempt = 0;
            void connect();
        },
        send,
    };
}
//# sourceMappingURL=realtime.js.map